import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  BookOpen, 
  BarChart3, 
  Settings, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Activity,
  Eye,
  Edit,
  Plus,
  Server,
  ArrowUpRight,
  
} from 'lucide-react';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Badge from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const { showError } = useNotification();
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboardData({ timeRange: selectedTimeRange });
      setDashboardData(response.data);
    } catch (error) {
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange, showError]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <ArrowUpRight className="w-4 h-4 text-gray-400" />;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { variant: 'success', label: 'Active' },
      inactive: { variant: 'secondary', label: 'Inactive' },
      pending: { variant: 'warning', label: 'Pending' },
      suspended: { variant: 'danger', label: 'Suspended' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      draft: { variant: 'secondary', label: 'Draft' },
      published: { variant: 'primary', label: 'Published' }
    };
    
    const config = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner text="Loading admin dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">System overview and management</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 3 months</option>
              <option value="1y">Last year</option>
            </select>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</span>
              </div>
              {getTrendIcon(dashboardData?.userGrowth)}
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(dashboardData?.totalUsers)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {dashboardData?.userGrowth >= 0 ? '+' : ''}
                {formatNumber(dashboardData?.newUsers)} new users
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Sessions</span>
              </div>
              {getTrendIcon(dashboardData?.sessionGrowth)}
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(dashboardData?.activeSessions)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {formatPercentage(dashboardData?.sessionGrowth)} vs last period
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Tests Completed</span>
              </div>
              {getTrendIcon(dashboardData?.testGrowth)}
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(dashboardData?.testsCompleted)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {formatNumber(dashboardData?.testsToday)} today
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg">
                  <Server className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">System Health</span>
              </div>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatPercentage(dashboardData?.systemHealth)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Passqik Educational Platform
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button 
              variant="primary" 
              onClick={() => navigate('/admin/users')}
              className="flex items-center justify-center space-x-2 p-4"
            >
              <Users className="w-5 h-5" />
              <span>Manage Users</span>
            </Button>
            <Button 
              variant="primary" 
              onClick={() => navigate('/admin/syllabus')}
              className="flex items-center justify-center space-x-2 p-4"
            >
              <BookOpen className="w-5 h-5" />
              <span>Manage Syllabi</span>
            </Button>
            <Button 
              variant="primary" 
              onClick={() => navigate('/admin/questions')}
              className="flex items-center justify-center space-x-2 p-4"
            >
              <Edit className="w-5 h-5" />
              <span>Manage Questions</span>
            </Button>
            <Button 
              variant="primary" 
              onClick={() => navigate('/admin/analytics')}
              className="flex items-center justify-center space-x-2 p-4"
            >
              <BarChart3 className="w-5 h-5" />
              <span>View Analytics</span>
            </Button>
          </div>
        </Card>


        {/* Recent Activity */}
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
            <Button variant="ghost" onClick={() => navigate('/admin/activity')}>
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {dashboardData?.recentActivity?.map((activity, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex-shrink-0">
                  <div className={cn(
                    "p-2 rounded-lg",
                    activity.type === 'user' && "bg-blue-100 dark:bg-blue-900/20",
                    activity.type === 'content' && "bg-green-100 dark:bg-green-900/20",
                    activity.type === 'system' && "bg-purple-100 dark:bg-purple-900/20",
                    activity.type === 'alert' && "bg-red-100 dark:bg-red-900/20"
                  )}>
                    {activity.type === 'user' && <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
                    {activity.type === 'content' && <BookOpen className="w-4 h-4 text-green-600 dark:text-green-400" />}
                    {activity.type === 'system' && <Settings className="w-4 h-4 text-purple-600 dark:text-purple-400" />}
                    {activity.type === 'alert' && <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.title}</p>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{activity.timeAgo}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{activity.description}</p>
                  {activity.user && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">by {activity.user}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(activity.status)}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* System Alerts */}
        {dashboardData?.systemAlerts && dashboardData.systemAlerts.length > 0 && (
          <Card className="p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Alerts</h3>
              <Badge variant="destructive">{dashboardData.systemAlerts.length}</Badge>
            </div>
            <div className="space-y-4">
              {dashboardData.systemAlerts.map((alert, index) => (
                <div key={index} className={cn(
                  "p-4 rounded-lg border-l-4",
                  alert.severity === 'high' && "bg-red-50 dark:bg-red-900/20 border-red-500",
                  alert.severity === 'medium' && "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500",
                  alert.severity === 'low' && "bg-blue-50 dark:bg-blue-900/20 border-blue-500"
                )}>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <AlertCircle className={cn(
                        "w-5 h-5",
                        alert.severity === 'high' && "text-red-600 dark:text-red-400",
                        alert.severity === 'medium' && "text-yellow-600 dark:text-yellow-400",
                        alert.severity === 'low' && "text-blue-600 dark:text-blue-400"
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{alert.title}</h4>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{alert.timeAgo}</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{alert.message}</p>
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                          Investigate
                        </Button>
                        <Button variant="ghost" size="sm">
                          Dismiss
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Badge variant={alert.severity === 'high' ? 'destructive' : alert.severity === 'medium' ? 'warning' : 'secondary'}>
                        {alert.severity}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Content Overview */}
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Content Overview</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-lg font-medium text-gray-900 dark:text-white">Questions</span>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(dashboardData?.totalQuestions)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{formatNumber(dashboardData?.pendingQuestions)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(dashboardData?.publishedQuestions)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Published</div>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Eye className="w-4 h-4 mr-2" />
                  Review
                </Button>
                <Button variant="primary" size="sm" className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  Add New
                </Button>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-lg font-medium text-gray-900 dark:text-white">User Management</span>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(dashboardData?.activeUsers)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(dashboardData?.newRegistrations)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">New Today</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{formatNumber(dashboardData?.suspendedUsers)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Suspended</div>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Eye className="w-4 h-4 mr-2" />
                  View All
                </Button>
                <Button variant="primary" size="sm" className="flex-1">
                  <Edit className="w-4 h-4 mr-2" />
                  Manage
                </Button>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-lg font-medium text-gray-900 dark:text-white">Performance</span>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatPercentage(dashboardData?.averageScore)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Avg Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatNumber(dashboardData?.completionRate)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Completion</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNumber(dashboardData?.engagementRate)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Engagement</div>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </Button>
                <Button variant="primary" size="sm" className="flex-1">
                  <Activity className="w-4 h-4 mr-2" />
                  Reports
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;